import React, {
  useState, useEffect, useMemo,
} from 'react';
import {
  Breadcrumb,
  Pagination,
  Table,
  Select,
  Spin,
} from 'antd';
import {
  BarcodeOutlined,
} from '@ant-design/icons';

import { useHistory, useParams, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import chunk from 'lodash.chunk';
import isArray from 'lodash.isarray';
import PageTitle from '../../lib/page-title';
import { useStore } from '../../../store';
import { useActions } from '../../providers/actions-provider/actions.context';

import {
  CREATE_REPORT_PATH,
  EDIT_REPORT_PATH,
  REPORTS_LIST_PATH,
  MAP_REPORT_PATH,
} from '../../routing/paths.constants';
import PrimaryButton from '../../lib/primary-button';
import SecondaryButton from '../../lib/secondary-button';
import BreadcrumbItem from '../../lib/breadcrumbItem';
import breadcrumbSeparator from '../../../assets/icons/breadcrumbSeparator.svg';
import './report.less';
import ExportModal from '../../lib/export-popup';
import ReportParams from '../edit-report/report-params';
import QrModal from '../../lib/qrModal';
import checkPermission from '../../../utils/permissions/permissions';
import { useFilterSortContextHook } from '../map-report/context/use-filter-sort-context';
import { mergeFilters, mergeSorts, syncedQueryParams } from '../edit-report/reportParamsHelpers';
import filterHiddenHeaders from './visible-headers';

interface ICrumb {
  path: string;
  breadcrumbName: string;
}

export interface IFilter {
  field: string,
  type: string,
  value: string
}

const pageSizeOptions = ['10', '12', '15', '20', '50', '100'];

const { Option } = Select;

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const Report = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const queryParams = useQuery();
  const filterSortContext = useFilterSortContextHook(id);
  const hasNoWritePermission = !checkPermission('reports', 'write');

  const {
    execReport,
    executedReport,
    listsForReport,
  } = useStore();

  const { reportActions } = useActions();
  const { location, push, replace } = useHistory();
  const [pageSize, setPageSize] = useState(12);
  const [skip, setSkip] = useState(0);
  const [exportVisible, setExportVisible] = useState(false);
  const [savedFilter, setSavedFilter] = useState<string | null>('');
  const [savedSort, setSavedSort] = useState('');
  const [barcodeModalVisible, setBarcodeModalVisible] = useState(false);
  const [barcode, setBarcode] = useState<Blob>();
  const [isLoading, setIsLoading] = useState(false);

  const execRepoUrl = location.pathname.split('/')[2];

  const uidHistory = useMemo(() => location.search.includes('uid')
    && location.search.includes('facility'),
  [location.pathname, location.search]);

  const loading = execReport.loading || executedReport.loading || listsForReport.loading
  || isLoading;
  const goToCreation = () => {
    push(`${REPORTS_LIST_PATH}${CREATE_REPORT_PATH}`);
  };

  const goToMap = () => {
    let url = `${REPORTS_LIST_PATH}${MAP_REPORT_PATH}/${execRepoUrl}/${execReport.data.code}`;

    const params = new URLSearchParams(queryParams.toString());
    if (!params.has('facility') && executedReport.data.find) {
      try {
        const parsed = JSON.parse(executedReport.data.find);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const findFacility = (obj: any): string | undefined => {
          if (!obj || typeof obj !== 'object') return undefined;
          const key = Object.keys(obj).find((k) => k.toLowerCase() === 'facility');
          return key ? obj[key]?.$eq : undefined;
        };
        const facility: string | undefined = parsed?.$and
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ? parsed.$and.map(findFacility).find((v: string | undefined) => v)
          : findFacility(parsed);
        if (facility) {
          params.set('facility', String(facility).replace(/"/g, ''));
        }
      } catch {
        // ignore parse errors — saved filter may be empty or malformed
      }
    }

    const qs = params.toString();
    if (qs) {
      url += `?${qs}`;
    }

    push(url);
  };
  const goToEdition = () => {
    push(`${REPORTS_LIST_PATH}${EDIT_REPORT_PATH}/exec-${executedReport.data.id}/base`);
  };

  useEffect(() => {
    reportActions.resetExecutedReport();
    reportActions.getExecutedReport(id);
  }, [id]);

  useEffect(() => {
    async function execLoad() {
      if (!filterSortContext.isDataReady) return;
      let mergedFilters = filterSortContext.findFilter;
      if (hasNoWritePermission) {
        mergedFilters = mergeFilters({
          serverFiltersString: filterSortContext.findFilter,
          userFiltersString: filterSortContext.userFilters!,
        });
      }
      let mergedSorts = filterSortContext.sort;
      if (hasNoWritePermission) {
        mergedSorts = mergeSorts({
          serverSortsString: filterSortContext.sort,
          userSortsString: filterSortContext.userSorts!,
        });
      }
      await reportActions.executeReport(
        id, pageSize, 0, mergedFilters, mergedSorts,
      );
      await reportActions.getListsForReport(`${executedReport.data.id}`);
      setSavedFilter(mergedFilters);
      setSavedSort(mergedSorts);
    }
    execLoad();
  },
  [id,
    filterSortContext.findFilter,
    filterSortContext.userFilters,
    filterSortContext.sort,
    filterSortContext.userSorts,
    filterSortContext.isDataReady,
  ]);

  useEffect(() => {
    setIsLoading(false);
  }, [barcode]);

  interface IKeys<value> {
    [id: string]: value;
  }
  const mappable = uidHistory || !!executedReport.data.mappable;
  const findTypeHeader = execReport.data?.headers?.indexOf('Type');
  const headers = [...execReport.data?.headers || []];
  const columnStyles = [...execReport.data?.columnStyles || []];

  if (findTypeHeader >= 0) {
    headers.splice(findTypeHeader, 1);
    columnStyles.splice(findTypeHeader, 1);
  }
  const openBarCodePopup = async (data: string) => {
    setIsLoading(true);
    const barcodeData = await reportActions.getBarCodeImage(data);
    setBarcodeModalVisible(true);
    setBarcode(barcodeData);
    setIsLoading(false);
  };
  // eslint-disable-next-line
  const buildColumnRender = (name: string) => (text: string, record: any, index: number) => {
    // eslint-disable-next-line react/destructuring-assignment
    const data = record[name];
    if (isArray(data)) {
      if (data[0] === 'image' && data[1].includes('barcode')) {
        return (
          <SecondaryButton
            type="button"
            onClick={async () => openBarCodePopup(data[1])}
          >
            <BarcodeOutlined />
          </SecondaryButton>
        );
      }
      const linkData = data[1];
      if (/\[.*\]/.test(linkData) && linkData.indexOf('http') > 0) {
        const x = linkData.indexOf('[');
        const y = linkData.indexOf(']');
        const i = linkData.indexOf('(');
        const j = linkData.indexOf(')');
        const isMarkDown = x >= 0 && y >= 0 && i >= 0 && j >= 0;
        const url = isMarkDown ? linkData.substring(i + 1, j) : linkData;
        const label = isMarkDown ? linkData.substring(x + 1, y) : data[0];
        // eslint-disable-next-line
        return <a className='table__link' target='__blank' href={url}>{label}</a>;
      }
      const isHistoryWithFacility = !!queryParams.get('facility') && data[1].includes('ASSET_HISTORY');
      const itemLink = isHistoryWithFacility ? `/reports-list/exec-${data[1]}&facility=${execReport.data.code}`
        : `/reports-list/exec-${data[1]}`;
      // eslint-disable-next-line
      return <a className='table__link'  href={itemLink}>{data[0]}</a>;
    }
    return text;
  };

  const visibleHeaders = filterHiddenHeaders(headers, executedReport.data.columns);

  const columnsHeaders = visibleHeaders.map((el, i) => ({
    title: el,
    key: `${el.toLowerCase}, ${i}`,
    dataIndex: el.toLowerCase(),
    textWrap: 'word-break',
    render: buildColumnRender(el.toLowerCase()),
  }));

  const finalReportData = execReport.data?.records?.map((arr) => {
    const row = {} as IKeys<string>;
    arr.forEach((value, index) => {
      row[execReport.data.headers[index].toLowerCase()] = value;
    });
    return row;
  });

  let dataForCrumbs: ICrumb[] = [];
  const initialCrumb = {
    path: REPORTS_LIST_PATH,
    breadcrumbName: 'Reports List',
  };
  if (location.pathname === REPORTS_LIST_PATH) {
    dataForCrumbs.push(initialCrumb);
  } else {
    // preparing routes for creation crumbs - without first three static '', 'reports-list', 'exec-'
    const prepareRoutes = location.pathname.split('/').slice(3);
    // break array by chunk of /name-of-nest and /id
    const chunks = chunk(prepareRoutes, 2);
    let path = REPORTS_LIST_PATH;
    const crumbsWithoutInitial = chunks.map((el) => {
      // each next path need to include previous
      path += `/${el[0]}/${el[1]}`;
      return {
        path,
        breadcrumbName: el[0],
      };
    });
    dataForCrumbs = [initialCrumb, ...crumbsWithoutInitial];
  }

  // Keep URL query params aligned with the submitted filter so that
  // deep-link params the user has just removed (e.g. ?facility=DFW)
  // don't get re-applied on refresh via `filtersFromQueryParams`.
  const syncQueryParamsWithFilter = (find: string): void => {
    const currentSearch = location.search.startsWith('?')
      ? location.search.slice(1)
      : location.search;
    const nextSearch = syncedQueryParams(currentSearch, find);
    if (nextSearch !== currentSearch) {
      replace(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`);
    }
  };

  const onSubmitAction = (find: string, sort: string) => {
    async function executeAction() {
      setSavedFilter(find);
      setSavedSort(sort);
      setSkip(0);
      syncQueryParamsWithFilter(find);
      await reportActions.executeReport(id, pageSize, 0, find, sort);
    }
    executeAction();
  };

  const onSaveAction = (find: string, sort: string) => {
    async function executeAction() {
      const reportId = executedReport.data.id;
      if (!reportId) return;
      await reportActions.updateExecutedReport(reportId, {
        id: reportId,
        tenant: executedReport.data.tenant,
        description: executedReport.data.description,
        columns: executedReport.data.columns,
        name: executedReport.data.name,
        code: executedReport.data.code,
        collection: executedReport.data.collection,
        aggregate: executedReport.data.aggregate,
        find,
        sort,
      });
      await reportActions.getExecutedReport(reportId);
      syncQueryParamsWithFilter(find);
    }
    executeAction();
  };

  const count = execReport.data.count || 0;
  return (
    <div className="execute-report__wrapper">
      {barcodeModalVisible && (
        <QrModal
          value={barcode}
          qrValue={undefined}
          visible={barcodeModalVisible}
          handler={() => { setBarcodeModalVisible(!barcodeModalVisible); setBarcode(undefined); }}
          isLoading={isLoading}
          initialHeight={350}
        />
      )}
      <Breadcrumb
        itemRender={BreadcrumbItem}
        routes={dataForCrumbs}
        separator={<img src={breadcrumbSeparator} alt="breadcrumb separator" />}
        params={{ dataForCrumbs }}
      />
      <Spin spinning={loading} size="large">
        {
          loading ? '' : (
            <PageTitle title={execReport.data.name} subTitle={execReport.data.description} back>
              <div className="wrapper-buttons">
                <SecondaryButton
                  title="Map"
                  onClick={goToMap}
                  disabled={!mappable}
                />
                <SecondaryButton
                  title="Export Report"
                  onClick={() => {
                    setExportVisible(true);
                  }}
                />
                { checkPermission('reports', 'write') && <SecondaryButton title="Edit Report" onClick={goToEdition} /> }
                { checkPermission('reports', 'write') && (
                  <PrimaryButton title="New Report" onClick={goToCreation} />
                )}
              </div>
            </PageTitle>
          )
        }
        <div className="table__wrapper">
          <div style={{ paddingBottom: 0 }} className="filter__wrapper execute-report__filter--wrapper">
            <ReportParams
              showCheckboxes
              showSave
              regex={listsForReport.data.regex}
              quickRanges={listsForReport.data.quickRanges}
              operators={listsForReport.data.operators}
              filterFields={listsForReport.data.filterFields}
              submitButtonLabel="Execute"
              notEditable={false}
              onSubmitAction={onSubmitAction}
              onSaveAction={onSaveAction}
              submitButtonAlwaysEnabled
              filtersString={filterSortContext.findFilter || ''}
              sortsString={executedReport.data.sort || ''}
              userFilters={filterSortContext.userFilters}
              userSorts={filterSortContext.userSorts}
              reportColumns={executedReport.data.columns}
              reportCollection={executedReport.data.collection}
              expanded={false}
              id={id}
            />
            {exportVisible && (
              <ExportModal
                setVisible={setExportVisible}
                code={executedReport.data.code}
                onBuildParams={() => ({ sort: savedSort, find: savedFilter || '' })}
              />
            )}

            <div className="column-items-size">
              <p className="column-items-size__text">
                {`Showing records ${count > 0 ? skip + 1 : 0} through ${count < skip + pageSize ? count : skip + pageSize} of ${count}`}
              </p>
              <div className="column-items-size__page-size">
                <span className="column-items-size__text">Page size</span>
                <Select
                  defaultValue={pageSize}
                  onChange={async (size) => {
                    setPageSize(+size);
                    await reportActions.executeReport(id, +size, skip, savedFilter, savedSort);
                  }}
                >
                  {
                    pageSizeOptions.map((option, i) => (
                      <Option
                        value={option}
                        key={`${option + i}`}
                      >
                        {option}
                      </Option>
                    ))
                  }

                </Select>
              </div>
            </div>
          </div>
          <Table
            className="table"
            columns={columnsHeaders}
            dataSource={finalReportData}
            bordered={false}
            loading={execReport.loading}
            rowKey={(record, i) => `${record.uid} ${i}`}
            pagination={false}
          />
          <Pagination
            className={classNames('custom-pagination', {
              none: !execReport.data.records?.length,
            })}
            current={Math.floor(skip / pageSize) + 1}
            showSizeChanger={false}
            pageSize={pageSize}
            hideOnSinglePage
            total={execReport.data.count}
            defaultPageSize={document.documentElement.clientHeight <= 800 ? 12 : 15}
            onChange={(page, size) => {
              if (size) {
                // eslint-disable-next-line @typescript-eslint/no-shadow
                const skip = (page - 1) * size;
                reportActions.executeReport(id, +size, skip, savedFilter, savedSort);
                setSkip(skip);
              }
            }}
          />
        </div>
      </Spin>
    </div>
  );
};

Report.displayName = 'Report';

export default Report;
